<template>
    <div class="sms-page dashboard-theme">
        <!-- 顶部条：**故意没有返回按钮**，这页是从地址直接进的，没有上级页面 -->
        <div class="top-bar">
            <div class="top-title">短信发送情况</div>
        </div>

        <!-- 阿里云账户余额：标签在左，金额靠右；低于 10 元标红 -->
        <div class="balance-bar" :class="{ 'balance-bar--low': isLowBalance }">
            <div class="balance-meta">
                <span class="balance-label">账户余额</span>
                <span v-if="isLowBalance" class="balance-warn">余额不足</span>
            </div>
            <div class="balance-amount">
                <span v-if="balanceCurrency" class="balance-currency">{{ balanceCurrency }}</span>
                <span class="balance-value" :class="{ 'balance-value--muted': !balanceCurrency }">{{ balanceAmount }}</span>
            </div>
        </div>

        <!-- 筛选：上课日期 + 状态 -->
        <div class="filter-bar">
            <div class="filter-btn" :class="{ 'filter-btn--on': !!date }" @click="openDate">
                <van-icon name="calendar-o" size="14" />
                <span class="filter-text">{{ dateLabel }}</span>
                <van-icon name="arrow-down" size="11" />
            </div>

            <div class="filter-btn" :class="{ 'filter-btn--on': status !== 'all' }" @click="showStatus = true">
                <van-icon name="filter-o" size="14" />
                <span class="filter-text">{{ statusLabel }}</span>
                <van-icon name="arrow-down" size="11" />
            </div>

            <div v-if="date || status !== 'all'" class="filter-clear" @click="clearFilters">清除</div>
        </div>

        <!-- 列表：下拉加载更多，不用页码 -->
        <div class="sms-container">
            <van-list v-model="loading" :finished="finished" finished-text="没有更多了" :error.sync="listError"
                error-text="加载失败，点击重试" @load="onLoad">
                <div class="sms-list">
                    <div v-for="item in list" :key="item.id" class="sms-item">
                        <!-- 头：编号在最左，标记和状态徽标推到最右 -->
                        <div class="sms-head">
                            <span class="sms-id">#{{ item.id }}</span>

                            <span class="sms-tags">
                                <!-- 学生已被删除。**只说明事实，不代表这就是失败的原因**：
                                     实测全部 267 条归档记录，学生删除时间都晚于计划发送
                                     时间 —— 没有一条是"先删了人、通知才到点" -->
                                <span v-if="item.archived" class="sms-tag">归档</span>

                                <span class="sms-badge" :style="badgeStyle(item.status)">
                                    {{ statusText(item.status) }}
                                </span>
                            </span>
                        </div>

                        <!-- 姓名和其余字段一样铺在内容区里；不用点开 -->
                        <div class="sms-fields">
                            <div class="field">
                                <span class="k">姓名</span>
                                <span class="v" :class="{ 'v--archived': item.archived }">{{ item.student }}</span>
                            </div>
                            <div class="field">
                                <span class="k">上课</span>
                                <span class="v">{{ item.lesson_time || '--' }}</span>
                            </div>
                            <div class="field">
                                <span class="k">发送</span>
                                <span class="v">{{ item.sent_at || '未发出' }}</span>
                            </div>
                            <div class="field">
                                <span class="k">手机</span>
                                <span class="v">{{ item.mobile }}</span>
                            </div>
                        </div>

                        <!-- 失败原因的人话说明（后端把阿里云 Code 翻好的）。
                             认不出来的码不给 hint，这里就不显示 -->
                        <div v-if="item.error_hint" class="sms-hint">
                            {{ item.error_hint }}
                        </div>

                        <!-- 只有真发出去过（成功/失败）才有原始返回。
                             过期的记录 message 是空的，点开也只会看到"没有原始返回"，
                             那就干脆不给这个按钮 -->
                        <!-- 判据用 message 而不是 raw_data：列表为了不传那个大字段，
                             没带 raw_data 正文 —— 而这两个字段在 SentNotice 里是同一次
                             update 写进去的，要么都有要么都没有（本地 518 条实测
                             零例外） -->
                        <div v-if="item.message" class="sms-foot">
                            <!-- 点一下打开；弹框里再点「关闭」收起 -->
                            <button type="button" class="raw-btn" @click="openRaw(item)">
                                {{ isRawOpen(item) ? '收起原始返回' : '原始返回' }}
                            </button>
                        </div>
                    </div>
                </div>
            </van-list>

            <div v-if="!loading && !list.length && !listError" class="sms-empty">
                <van-empty description="没有符合条件的记录" />
            </div>

            <div v-if="list.length" class="sms-count">共 {{ total }} 条</div>
        </div>

        <!-- 上课日期选择 -->
        <!--
            ⚠️ 这里**不能用 van-calendar**。它的样式是 `height: 100%`，
            而带 :poppable="false" 内联渲染时外面没有给定高度的父元素 ——
            它会一路撑到屏幕外，把自己的「确认」按钮顶出可视区，
            于是**弹出来就关不掉**（遮罩也被盖住了）。

            van-datetime-picker 是固定高度的滚轮，自带「取消 / 确认」工具条，
            小、且有明确的出口。
        -->
        <van-popup v-model="showDate" position="bottom" round>
            <van-datetime-picker v-model="pickerValue" type="date" title="按上课日期筛选"
                :min-date="minDate" :max-date="maxDate" confirm-button-text="确定"
                cancel-button-text="取消" @confirm="onDateConfirm" @cancel="showDate = false" />
        </van-popup>

        <!-- 状态选择 -->
        <van-popup v-model="showStatus" position="bottom" round>
            <van-picker show-toolbar title="按状态筛选" :columns="statusColumns" @confirm="onStatusConfirm"
                @cancel="showStatus = false" />
        </van-popup>

        <!-- 原始返回 -->
        <van-popup v-model="showRaw" position="bottom" round :style="{ height: '62%' }">
            <div class="raw-wrap">
                <div class="raw-head">
                    <span class="raw-title">原始返回 · #{{ rawId }}</span>
                    <van-icon name="cross" size="18" color="#9ca3af" @click="showRaw = false" />
                </div>

                <div class="raw-body">
                    <div v-if="rawLoading" class="raw-loading">
                        <van-loading size="18px" color="#2563eb">加载中…</van-loading>
                    </div>

                    <!-- 格式化成「字段 — 值」逐行靠左，比直接甩一串 JSON 好读 -->
                    <div v-else-if="rawRows.length" class="raw-list">
                        <div v-for="row in rawRows" :key="row.key" class="raw-item">
                            <span class="raw-key">{{ row.key }}</span>
                            <span class="raw-val">{{ row.value }}</span>
                        </div>
                    </div>

                    <!-- raw_data 为空有两种情况，得说清楚，不然会以为是页面坏了 -->
                    <div v-else class="raw-empty">
                        这条没有原始返回。<br />
                        说明发出去之前就出错了（网络超时之类），阿里云那边根本没有响应 ——
                        具体原因只在服务器日志里。
                    </div>
                </div>

                <div class="raw-foot">
                    <van-button block round size="small" type="primary" :disabled="!rawText" @click="copyRaw">
                        复制
                    </van-button>
                    <van-button block round size="small" plain @click="showRaw = false">关闭</van-button>
                </div>
            </div>
        </van-popup>
    </div>
</template>

<script>
import axios from "axios";

/** 状态 → 文案。颜色只有三种：绿=成功，红=失败/过期，黄=待发 */
const STATUS_TEXT = {
    success: "成功",
    failed: "失败",
    expired: "过期",
    pending: "待发"
};

const STATUS_STYLE = {
    success: { color: "#16a34a", background: "#dcfce7" },
    failed: { color: "#dc2626", background: "#fee2e2" },
    // 「过期」= 计划时间过了但一次都没发出去。和「失败」都标红，
    // 靠文案区分：失败是尝试过被拒，过期是压根没发
    expired: { color: "#dc2626", background: "#fee2e2" },
    pending: { color: "#d97706", background: "#fef3c7" }
};

export default {
    name: "SmsStateView",
    data() {
        return {
            perPage: 10,

            // 每页 10 条，滚到底自动加载下一页
            page: 1,
            list: [],
            total: 0,
            // 阿里云账户余额（后端缓存 5 分钟），null = 暂时获取不到
            balance: null,
            loading: false,
            finished: false,
            listError: false,
            fetching: false,

            // 筛选条件（日期按**上课时间**筛）
            date: "",
            status: "all",
            showDate: false,
            // 滚轮当前停留的日期。每次打开时重置成当前筛选值（没筛就是今天）
            pickerValue: new Date(),
            showStatus: false,
            statusColumns: [
                { text: "全部状态", value: "all" },
                { text: "成功", value: "success" },
                { text: "失败", value: "failed" },
                { text: "待发", value: "pending" },
                { text: "过期", value: "expired" }
            ],

            // 原始返回：点按钮才去拉，拉过就缓存
            showRaw: false,
            rawId: null,
            rawObject: null,
            rawLoading: false,
            rawCache: {},

            // 最早的数据是 2022 年；向后留一点余量给"还没到点"的预约
            minDate: new Date(2022, 0, 1),
            maxDate: new Date(Date.now() + 90 * 86400000)
        };
    },
    computed: {
        dateLabel() {
            return this.date || "全部日期";
        },
        statusLabel() {
            const hit = this.statusColumns.find(c => c.value === this.status);
            return hit ? hit.text : "全部状态";
        },

        /** 金额：加千分位并固定两位小数；拿不到就显示一个安静的破折号 */
        balanceAmount() {
            if (!this.balance || !this.balance.available) return "—";
            const value = Number(this.balance.available);
            if (!Number.isFinite(value)) return this.balance.available;
            return value.toLocaleString("zh-CN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        /** 人民币显示 ¥，其他币种显示代码 */
        balanceCurrency() {
            if (!this.balance || !this.balance.available) return "";
            return this.balance.currency === "CNY" ? "¥" : this.balance.currency;
        },

        /** 低于 10 元：短信随时可能因欠费失败 */
        isLowBalance() {
            if (!this.balance || !this.balance.available) return false;
            const value = parseFloat(this.balance.available);
            return !Number.isNaN(value) && value < 10;
        },

        /** 原始返回逐行拆开显示 */
        rawRows() {
            if (!this.rawObject) return [];

            return Object.keys(this.rawObject).map(key => ({
                key,
                value: String(this.rawObject[key])
            }));
        },

        /** 复制用的还是完整 JSON —— 粘到别处排查时那个更有用 */
        rawText() {
            return this.rawObject ? JSON.stringify(this.rawObject, null, 2) : "";
        }
    },
    methods: {
        onLoad() {
            this.load(this.page);
        },

        load(page) {
            if (this.fetching) return;
            this.fetching = true;

            // 这个接口是**公开**的（不要求登录），所以不带 Authorization ——
            // 网页前端只有学生登录，而这是给管理员看的页面，不该逼人先拿
            // 学生账号登录。防护靠"地址不公开 + 服务端脱敏"。
            axios
                .get("/api/sms-state", {
                    params: { page, status: this.status, date: this.date }
                })
                .then(response => {
                    const data = response.data && response.data.data;

                    if (!data || !Array.isArray(data.list)) {
                        this.listError = true;
                        return;
                    }

                    this.list = page === 1 ? data.list : this.list.concat(data.list);
                    this.total = data.total || 0;
                    // 每次响应都会带余额（后端缓存），旧后端没有这个字段就别覆盖
                    if (data.balance !== undefined) {
                        this.balance = data.balance;
                    }
                    this.page = page + 1;

                    if (data.list.length < this.perPage) {
                        this.finished = true;
                    }
                })
                .catch(err => {
                    const code = err.response && err.response.status;

                    // 这一页没拿到内容 → 让 van-list 显示"点击重试"
                    this.listError = true;

                    if (code === 404) {
                        this.$toast("接口不存在（404），服务端可能还没部署这一版");
                    }
                })
                .then(() => {
                    this.fetching = false;
                    this.loading = false;
                });
        },

        /** 换筛选条件：清空重来 */
        reload() {
            this.page = 1;
            this.list = [];
            this.total = 0;
            this.finished = false;
            this.listError = false;
            this.loading = true;

            this.load(1);
        },

        openDate() {
            // 每次打开都从当前筛选值起步，别停在上次滚到的地方
            this.pickerValue = this.date ? this.parseDate(this.date) : new Date();
            this.showDate = true;
        },

        onDateConfirm(value) {
            this.date = this.formatDate(value);
            this.showDate = false;
            this.reload();
        },

        onStatusConfirm(column) {
            this.status = column.value;
            this.showStatus = false;
            this.reload();
        },

        clearFilters() {
            this.date = "";
            this.status = "all";
            this.reload();
        },

        /** Date → YYYY-MM-DD（后端只认这个格式，补零不能省） */
        formatDate(value) {
            const d = value instanceof Date ? value : new Date(value);
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");

            return `${d.getFullYear()}-${mm}-${dd}`;
        },

        /**
         * YYYY-MM-DD → Date（本地时区的当天零点）
         *
         * 别偷懒写 `new Date("2026-09-04")` —— 那是按 UTC 解析的，
         * 换个时区就可能串到前一天去。手动拼不会出错。
         */
        parseDate(text) {
            const [y, m, d] = String(text).split("-").map(Number);

            return new Date(y, (m || 1) - 1, d || 1);
        },

        isRawOpen(item) {
            return this.showRaw && this.rawId === item.id;
        },

        openRaw(item) {
            // 再点一下关上
            if (this.isRawOpen(item)) {
                this.showRaw = false;
                return;
            }

            this.rawId = item.id;
            this.showRaw = true;

            if (this.rawCache[item.id] !== undefined) {
                this.rawObject = this.rawCache[item.id];
                return;
            }

            this.rawObject = null;
            this.rawLoading = true;

            axios
                .get(`/api/sms-state/${item.id}`)
                .then(response => {
                    const data = response.data && response.data.data;
                    const raw = (data && data.raw_data) || null;

                    this.rawCache[item.id] = raw;
                    // 拉回来时用户可能已经关掉、或又点了别条，别覆盖
                    if (this.rawId === item.id) {
                        this.rawObject = raw;
                    }
                })
                .catch(() => {
                    if (this.rawId === item.id) {
                        this.rawObject = null;
                    }
                })
                .then(() => {
                    this.rawLoading = false;
                });
        },

        copyRaw() {
            const text = this.rawText;
            if (!text) return;

            const done = () => this.$toast.success("已复制");
            const fail = () => this.$toast("复制失败，长按上面的文本手动复制");

            // https 下（生产就是）走标准 API
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(done).catch(fail);
                return;
            }

            // 老浏览器 / 非 https 的兜底
            try {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                done();
            } catch (e) {
                fail();
            }
        },

        statusText(status) {
            return STATUS_TEXT[status] || status;
        },

        badgeStyle(status) {
            return STATUS_STYLE[status] || STATUS_STYLE.pending;
        }
    }
};
</script>

<style scoped>
.dashboard-theme {
    min-height: 100vh;
    background:
        radial-gradient(circle at 0 0, #e6edff 0, transparent 45%),
        radial-gradient(circle at 100% 0, #fef3c7 0, transparent 45%),
        linear-gradient(to bottom, #f4f5fb 0, #f9fafb 100%);
    box-sizing: border-box;
}

/* 顶部条：只有标题，没有返回 */
.top-bar {
    padding: 14px 16px 10px;
    text-align: center;
}

.top-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

/* 账户余额：标签在左，金额靠右 */
.balance-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 16px 10px;
    padding: 11px 14px;
    background: #ffffff;
    border: 1px solid #eef1f7;
    border-radius: 12px;
    box-sizing: border-box;
}

.balance-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.balance-label {
    font-size: 12px;
    color: #6b7280;
}

.balance-warn {
    padding: 2px 7px;
    border-radius: 999px;
    background: #fdecea;
    color: #d93025;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.5;
    white-space: nowrap;
}

.balance-amount {
    display: flex;
    align-items: baseline;
    gap: 2px;
    margin-left: auto;
}

.balance-currency {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
}

.balance-value {
    font-size: 17px;
    font-weight: 600;
    color: #111827;
    letter-spacing: -0.2px;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.balance-bar--low .balance-value {
    color: #d93025;
}

.balance-value--muted {
    color: #9ca3af;
    font-size: 15px;
    font-weight: 500;
}

/* 筛选条 */
.filter-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px 10px;
    box-sizing: border-box;
}

.filter-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
    background: #ffffff;
    border-radius: 10px;
    padding: 8px 10px;
    color: #4b5563;
    box-shadow: 0 0 0 1px rgba(226, 232, 240, 0.95);
    box-sizing: border-box;
}

.filter-btn--on {
    color: #2563eb;
    background: #eff4ff;
    box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.35);
}

.filter-text {
    flex: 1;
    font-size: 13px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.filter-clear {
    flex: 0 0 auto;
    font-size: 13px;
    color: #2563eb;
    padding: 0 4px;
}

/* 列表 */
.sms-container {
    padding: 0 16px 28px;
    box-sizing: border-box;
}

.sms-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
}

.sms-item {
    background: #ffffff;
    border-radius: 12px;
    padding: 10px 12px;
    box-shadow:
        0 8px 20px rgba(148, 163, 184, 0.18),
        0 0 0 1px rgba(226, 232, 240, 0.9);
    box-sizing: border-box;
}

/* 头：编号在最左，徽标推到最右 */
.sms-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}

.sms-id {
    font-size: 12px;
    color: #9ca3af;
    font-family: ui-monospace, Menlo, Consolas, monospace;
    /* 显式左对齐 */
    text-align: left;
}

.sms-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
}

/* 「归档」标记：中性灰，**刻意不用状态色** —— 它只是说明这个学生已被删除，
   不是一种发送状态，别让人误以为红色的原因在它 */
.sms-tag {
    font-size: 11px;
    padding: 1px 7px;
    border-radius: 999px;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    white-space: nowrap;
}

.sms-badge {
    font-size: 11.5px;
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: 600;
    white-space: nowrap;
    flex: 0 0 auto;
    align-self: center;
}

/* 字段：标签定宽在左，值左对齐跟在后面 —— 紧凑且长文案不会歪 */
.sms-fields {
    padding-top: 2px;
}

.field {
    display: flex;
    gap: 8px;
    padding: 2px 0;
    font-size: 12.5px;
    line-height: 1.65;
}

.k {
    flex: 0 0 32px;
    color: #a8aeb8;
}

.v {
    flex: 1;
    min-width: 0;
    color: #374151;
    text-align: left;
    word-break: break-all;
}

/* 学生已被删除 → 显示"存档"，弱化色，别看着像个正常姓名 */
.v--archived {
    color: #9ca3af;
}

/* 失败原因的说明。做成整块带底色 —— 尤其是"余额不足"那条，
   是这个页面最想让人一眼看见的东西 */
.sms-hint {
    margin-top: 8px;
    padding: 7px 10px;
    border-radius: 8px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 12.5px;
    line-height: 1.6;
    text-align: left;
}

/* 原始返回按钮 */
.sms-foot {
    display: flex;
    justify-content: flex-end;
    margin-top: 6px;
}

.raw-btn {
    border: none;
    background: #f1f5ff;
    color: #2563eb;
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 999px;
    line-height: 1.4;
}

.raw-btn:active {
    background: #e2ebff;
}

.sms-empty {
    padding-top: 30px;
}

.sms-count {
    text-align: center;
    font-size: 12px;
    color: #9ca3af;
    padding-top: 16px;
}

/* 原始返回弹框 */
.raw-wrap {
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
}

.raw-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 10px;
    border-bottom: 1px solid #eef0f4;
}

.raw-title {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
}

.raw-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
}

.raw-loading {
    display: flex;
    justify-content: center;
    padding: 40px 0;
}

/* 原始返回：字段逐行铺开，**全部靠左**，别居中 */
.raw-list {
    text-align: left;
}

.raw-item {
    display: flex;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
    font-size: 12.5px;
    line-height: 1.6;
    text-align: left;
}

.raw-item:last-child {
    border-bottom: none;
}

.raw-key {
    flex: 0 0 86px;
    color: #9ca3af;
    text-align: left;
    font-family: ui-monospace, Menlo, Consolas, monospace;
}

.raw-val {
    flex: 1;
    min-width: 0;
    color: #374151;
    text-align: left;
    word-break: break-all;
    font-family: ui-monospace, Menlo, Consolas, monospace;
}

.raw-empty {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 10px;
    padding: 12px;
    font-size: 12.5px;
    color: #92400e;
    line-height: 1.8;
}

.raw-foot {
    display: flex;
    gap: 10px;
    padding: 10px 16px 16px;
    border-top: 1px solid #eef0f4;
}

@media (max-width: 375px) {
    .filter-bar {
        padding: 0 12px 10px;
    }

    .sms-container {
        padding: 0 12px 24px;
    }
}
</style>
